import { Component, input, signal, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import {
  AsyncProgress,
  FileAttachment,
  isBrowserPreviewable,
  forceDownloadFile,
  getFileIconClass,
  formatFileSize,
} from '../ai-chat.config';

/**
 * @file chat-async-progress-card.component.ts
 * @description Renders a live progress bar, generated files, and task step breakdown for long-running asynchronous workflows.
 */
@Component({
  selector: 'rapider-chat-async-progress-card',
  standalone: true,
  imports: [
    CommonModule,
    NzProgressModule,
    NzTimelineModule,
    MarkdownPreviewComponent,
  ],
  template: `
    <div
      class="@container mt-4 w-full bg-component-inner border border-secondary-border rounded-xl shadow-sm overflow-hidden min-w-[280px] sm:min-w-[400px]"
    >
      <div class="p-4 bg-alt-background border-b border-secondary-border">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <i
              class="fas"
              [ngClass]="
                progress().percentage === 100
                  ? 'fa-check-circle text-success'
                  : 'fa-circle-notch fa-spin text-primary'
              "
            ></i>
            <span class="text-sm font-bold text-heading">
              {{
                progress().statusText && progress().statusText !== 'undefined'
                  ? progress().statusText
                  : 'Processing...'
              }}
            </span>
          </div>
          <span class="text-xs font-bold text-primary"
            >{{ progress().percentage }}%</span
          >
        </div>
        <nz-progress
          [nzPercent]="progress().percentage"
          [nzStatus]="progress().percentage === 100 ? 'success' : 'active'"
          [nzShowInfo]="false"
          nzSize="small"
        ></nz-progress>
      </div>

      @if (attachments() && attachments()!.length > 0) {
        <div
          class="p-4 border-b border-secondary-border flex flex-col gap-2 bg-body-background"
        >
          <span
            class="text-[10px] font-bold text-secondary-text uppercase tracking-wider mb-1"
            i18n="@@generatedFilesLabel"
            >Generated Files</span
          >
          @for (file of attachments(); track file.id) {
            <div
              class="flex items-center justify-between p-3 bg-component-inner border border-secondary-border rounded-lg hover:border-primary transition-colors group"
            >
              <div class="flex items-center gap-3 overflow-hidden">
                <div
                  class="w-8 h-8 rounded bg-primary/10 flex items-center justify-center shrink-0"
                >
                  <i
                    class="fas text-primary text-lg"
                    [ngClass]="getIcon(file.name, file.mimeType)"
                  ></i>
                </div>
                <div class="flex flex-col min-w-0">
                  <span
                    class="text-sm font-medium text-primary-text truncate"
                    [title]="file.name"
                    >{{ file.name }}</span
                  >
                  <span class="text-[10px] text-secondary-text uppercase">
                    {{
                      file.mimeType && file.mimeType !== 'undefined'
                        ? file.mimeType
                        : file.extension || 'FILE'
                    }}
                    - {{ formatSize(file.size) }}
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-2">
                @if (isPreviewable(file)) {
                  <button
                    (click)="openInNewTab(file, $event)"
                    class="w-8 h-8 flex items-center justify-center rounded-full bg-alt-background text-secondary-text group-hover:text-primary group-hover:bg-primary/10 transition-colors focus:outline-none"
                    i18n-title="@@openInNewTabTitle"
                    title="Open in new tab"
                  >
                    <i class="fas fa-external-link-alt"></i>
                  </button>
                }
                <button
                  (click)="triggerDownload(file, $event)"
                  class="w-8 h-8 flex items-center justify-center rounded-full bg-alt-background text-secondary-text group-hover:text-primary group-hover:bg-primary/10 transition-colors focus:outline-none"
                  i18n-title="@@downloadFileTitle"
                  title="Download file"
                >
                  <i class="fas fa-download"></i>
                </button>
              </div>
            </div>
          }
        </div>
      }

      @if (progress().steps && progress().steps!.length > 0) {
        <div class="bg-component-inner">
          <div
            class="p-3 flex items-center justify-between cursor-pointer hover:bg-alt-background transition-colors select-none"
            (click)="isLogsExpanded.set(!isLogsExpanded())"
          >
            <span
              class="text-[11px] font-bold text-secondary-text uppercase tracking-wider flex items-center gap-2"
            >
              <i class="fas fa-list-ul"></i>
              <ng-container i18n="@@viewExecutionLogs"
                >Workflow Step Logs</ng-container
              >
            </span>
            <i
              class="fas text-secondary-text transition-transform duration-300 text-xs"
              [ngClass]="isLogsExpanded() ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
          </div>

          @if (isLogsExpanded()) {
            <div
              class="p-4 border-t border-secondary-border bg-alt-background animate-fade-in"
            >
              @if (hasDummyLogsOnly()) {
                <div class="flex flex-col items-center justify-center p-2">
                  <span
                    class="text-sm text-secondary-text mb-3 text-center"
                    i18n="@@fetchDetailedLogsPrompt"
                    >Detailed logs are not loaded.</span
                  >
                  <button
                    (click)="onFetchLogsClicked()"
                    [disabled]="isFetchingLogs()"
                    class="px-4 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    i18n="@@fetchLogsButton"
                  >
                    @if (isFetchingLogs()) {
                      <i class="fas fa-circle-notch fa-spin mr-2"></i>
                      Fetching...
                    } @else {
                      Fetch Logs
                    }
                  </button>
                </div>
              } @else {
                <nz-timeline>
                  @for (
                    step of progress().steps;
                    track $index;
                    let stepIndex = $index
                  ) {
                    <nz-timeline-item
                      [nzColor]="
                        step.status === 'completed'
                          ? 'green'
                          : step.status === 'error' || step.status === 'failed'
                            ? 'red'
                            : 'blue'
                      "
                    >
                      <div class="flex flex-col gap-1 w-full min-w-0">
                        <div
                          class="flex items-start justify-between gap-2 w-full pb-1"
                        >
                          <div class="min-w-0 flex items-center pt-0.5">
                            <span
                              class="text-sm text-primary-text font-medium break-words"
                              >{{ step.title }}</span
                            >
                            @if (step.status === 'active') {
                              <i
                                class="fas fa-circle-notch fa-spin text-primary ml-2 text-xs shrink-0"
                              ></i>
                            }
                          </div>
                          @if (step.resultMarkdown || step.result) {
                            <button
                              (click)="toggleStep(stepIndex, $event)"
                              class="shrink-0 text-[10px] px-2 py-1 bg-alt-background border border-secondary-border rounded shadow-sm font-bold text-secondary-text hover:text-primary hover:border-primary transition-colors focus:outline-none uppercase tracking-wider whitespace-nowrap"
                            >
                              @if (isStepExpanded(stepIndex)) {
                                <ng-container i18n="@@hideStepResponse"
                                  >Hide Response</ng-container
                                >
                              } @else {
                                <ng-container i18n="@@displayStepResponse"
                                  >Display Response</ng-container
                                >
                              }
                            </button>
                          }
                        </div>

                        @if (
                          isStepExpanded(stepIndex) &&
                          (step.resultMarkdown || step.result)
                        ) {
                          <div
                            class="mt-1 p-3 bg-alt-background border border-secondary-border rounded-lg w-full max-w-full overflow-hidden animate-fade-in shadow-inner"
                          >
                            @if (step.resultMarkdown) {
                              <rapider-markdown-preview
                                [data]="step.resultMarkdown!"
                                [viewMode]="'compact'"
                                [showHeader]="false"
                                [showBorder]="false"
                                [isTransparent]="true"
                              ></rapider-markdown-preview>
                            } @else {
                              <pre
                                class="text-[11px] text-primary-text m-0 custom-scrollbar overflow-x-auto w-full max-h-[300px]"
                                >{{ step.result | json }}</pre
                              >
                            }
                          </div>
                        }
                      </div>
                    </nz-timeline-item>
                  }
                </nz-timeline>
              }
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class ChatAsyncProgressCardComponent {
  public progress = input.required<AsyncProgress>();
  public attachments = input<FileAttachment[] | undefined>(undefined);

  public isLogsExpanded = signal<boolean>(true);
  public isFetchingLogs = signal<boolean>(false);
  public expandedSteps = signal<Set<number>>(new Set<number>());

  public fetchLogs = output<string>();

  public getIcon = getFileIconClass;
  public formatSize = formatFileSize;

  /**
   * Toggles the visibility of a single step's response block inside the timeline.
   * Keyed by the step's row index so steps sharing a title do not toggle together.
   * Keeps an immutable Set in a signal so the template re-renders on change.
   * @param {number} stepIndex - The row index used as the identity key of the step.
   * @param {Event} event - The originating click event, stopped so the card does not collapse.
   * @returns {void}
   */
  public toggleStep(stepIndex: number, event: Event): void {
    event.stopPropagation();
    const current = new Set(this.expandedSteps());
    if (current.has(stepIndex)) {
      current.delete(stepIndex);
    } else {
      current.add(stepIndex);
    }
    this.expandedSteps.set(current);
  }

  /**
   * Indicates whether a given step's response block is currently expanded.
   * @param {number} stepIndex - The row index used as the identity key of the step.
   * @returns {boolean} True when the step's response is visible.
   */
  public isStepExpanded(stepIndex: number): boolean {
    return this.expandedSteps().has(stepIndex);
  }

  public hasDummyLogsOnly(): boolean {
    const steps = this.progress().steps;
    return (
      !!steps &&
      steps.length === 1 &&
      steps[0].title.toLowerCase().includes('background process completed')
    );
  }

  public onFetchLogsClicked(): void {
    const correlationId = this.progress().correlationId;
    if (correlationId) {
      this.isFetchingLogs.set(true);
      this.fetchLogs.emit(correlationId);
    }
  }

  public isPreviewable(file: FileAttachment): boolean {
    return isBrowserPreviewable(
      file.mimeType,
      file.name,
      file.extension,
      file.url,
    );
  }

  public async triggerDownload(
    file: FileAttachment,
    event: Event,
  ): Promise<void> {
    event.stopPropagation();
    if (file.url) {
      await forceDownloadFile(file.url, file.name);
    }
  }

  public openInNewTab(file: FileAttachment, event: Event): void {
    event.stopPropagation();
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }
}
